import React from 'react';
import { Box, Typography, Paper, Stack, Chip, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SilentMentorNudge from '../components/SilentMentorNudge';
import { generateNudge } from '../services/silentMentorService';

export default function ExamSummary({ exam, answers }) {
  // Simple evaluation: correct answers are in question.correct
  const results = exam.questions.map(q => {
    const userAns = answers[q.id];
    return {
      ...q,
      userAns,
      correct: userAns === q.correct,
    };
  });
  const score = results.filter(r => r.correct).length;
  // build topic-level stats when available
  const topicStats = results.reduce((acc, r) => {
    const t = r.topic || 'General';
    if (!acc[t]) acc[t] = { total: 0, correct: 0 };
    acc[t].total += 1;
    if (r.correct) acc[t].correct += 1;
    return acc;
  }, {});

  const topics = Object.keys(topicStats).map(k => ({
    topic: k,
    total: topicStats[k].total,
    accuracy: topicStats[k].correct / topicStats[k].total,
  }));

  const MIN_TOPIC_QUESTIONS = 2;
  const eligible = topics.filter(t => t.total >= MIN_TOPIC_QUESTIONS);
  const best = eligible.slice().sort((a, b) => b.accuracy - a.accuracy)[0];
  const worst = eligible.slice().sort((a, b) => a.accuracy - b.accuracy)[0];

  const navigate = useNavigate();

  const nudgeSpec = generateNudge({
    score,
    total: results.length,
    topics,
    examId: exam.id,
  });

  const handleNudgeAction = () => {
    // default routing: if worst topic present, deep-link to that topic
    const worstTopic = (nudgeSpec && nudgeSpec.message && topics && topics.length && topics.sort((a,b)=>a.accuracy-b.accuracy)[0]) || null;
    if (worstTopic && worstTopic.topic) {
      navigate(`/subject-practice?topic=${encodeURIComponent(worstTopic.topic)}`);
    } else {
      navigate('/subject-practice');
    }
  };
  return (
    <Box maxWidth="md" mx="auto" py={4}>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h5">Exam Complete!</Typography>
        <Typography variant="h6" color="primary">Score: {score} / {results.length}</Typography>
      </Paper>
      <SilentMentorNudge
        nudgeId={nudgeSpec.nudgeId}
        title={nudgeSpec.title}
        message={nudgeSpec.message}
        actionLabel={nudgeSpec.actionLabel}
        onAction={handleNudgeAction}
        meta={{ score, total: results.length, variant: nudgeSpec.variant }}
      />
      <Stack spacing={2}>
        {results.map((r, i) => (
          <Paper key={r.id} sx={{ p: 2 }}>
            <Typography>Q{i + 1}: {r.text}</Typography>
            <Stack direction="row" spacing={1} alignItems="center" mt={1}>
              <Chip label={`Your Answer: ${r.userAns || 'None'}`} color={r.correct ? 'success' : 'error'} />
              <Chip label={`Correct: ${r.correct ? 'Yes' : r.correct === false ? r.correctAns : ''}`} />
              {!r.correct && r.correct && <Chip label={`Correct Answer: ${r.correct}`} color="info" />}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
